import type { Ref } from "react";
import {
  Checkbox as HeadlessUICheckbox,
  Description,
  Field,
  Label as HeadlessUILabel,
} from "@headlessui/react";
import { tv } from "tailwind-variants";

import { Tooltip } from "@/ui";
import { Message } from "./message";

const checkboxVariants = tv({
  slots: {
    container: "flex flex-col gap-1",
    row: "flex items-center gap-2",
    tick: "size-3.5 stroke-[2.5] transition-opacity duration-150",
    label: "text-sm text-content",
    checkbox: [
      "inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded border",
      "transition-[background-color,border-color,box-shadow] duration-150",
      "focus:outline-none data-focus-visible:ring-2 data-focus-visible:ring-focus-ring data-focus-visible:ring-offset-2 data-focus-visible:ring-offset-canvas",
      "data-disabled:cursor-not-allowed data-disabled:opacity-60",
    ],
  },
  variants: {
    checked: {
      true: {
        tick: "stroke-content-on-accent opacity-100",
        checkbox: "border-accent bg-accent",
      },
      false: {
        tick: "opacity-0",
        checkbox: "border-line-control bg-surface hover:border-content-subtle",
      },
    },
    error: {
      true: {
        checkbox: "border-danger",
      },
    },
    disabled: {
      true: { label: "cursor-not-allowed opacity-60" },
      false: { label: "cursor-pointer font-normal" },
    },
  },
});

const { checkbox, container, label: labelClass, row, tick } = checkboxVariants();

export type CheckboxProps = {
  id: string;
  "aria-label"?: string;
  label?: string;
  error?: string | boolean;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  containerClassName?: string;
  className?: string;
  ref?: Ref<HTMLElement> | undefined;
  disabledLegend?: string;
  hideMessage?: boolean;
};

type TickProps = {
  checked: boolean;
};

const Tick = ({ checked }: TickProps) => {
  return (
    <svg aria-hidden="true" className={tick({ checked })} fill="none" viewBox="0 0 14 14">
      <path d="M3 8L6 11L11 3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const Checkbox = ({
  "aria-label": ariaLabel,
  checked = false,
  className,
  containerClassName,
  disabled,
  disabledLegend,
  error,
  hideMessage = false,
  id,
  label,
  onChange,
  ref,
}: CheckboxProps) => {
  const tooltipEnabled = disabled && Boolean(disabledLegend);
  const hasError = Boolean(error);

  return (
    /*
      Headless UI renders the checkbox as `span[role="checkbox"]`, which `for`
      cannot target and whose `aria-describedby` it owns, so the association has
      to come from its own Field/Label/Description rather than from ours.
    */
    <Field className={container({ className: containerClassName })} disabled={disabled}>
      <div className={row()}>
        <Tooltip content={disabledLegend ?? ""} disabled={!tooltipEnabled}>
          <HeadlessUICheckbox
            aria-invalid={hasError || undefined}
            aria-label={ariaLabel}
            checked={checked}
            className={({ checked }) => {
              return checkbox({ checked, error: hasError, className });
            }}
            disabled={disabled}
            id={id}
            onChange={onChange}
            ref={ref}
          >
            {({ checked }) => {
              return <Tick checked={checked} />;
            }}
          </HeadlessUICheckbox>
        </Tooltip>

        {label ? (
          <HeadlessUILabel className={labelClass({ disabled: Boolean(disabled) })}>
            {label}
          </HeadlessUILabel>
        ) : null}
      </div>

      {hideMessage ? null : <Description as={Message} error={error} />}
    </Field>
  );
};
